import { useEffect, useRef, useState } from "react";
import { SettingsHead } from "../components/AccountTabs";
import { supabase } from "../lib/supabase";
import { Workspace } from "../components/Shell";
import {
  NAME_LIMIT,
  describeIdentity,
  faultTone,
  nameFaultMessage,
  saveState,
  withdrawal,
  type ConsentDraft,
} from "../lib/identity";

/* THE PUBLIC IDENTITY — the name and face a coach shows the community (W6, 2026-09-18).
 *
 * 🔴 **W6 IS THE COMMUNITY, AND THIS IS THE PART OF IT THAT CAN HONESTLY SHIP ALONE.** Posts,
 * comments, likes and follows come *"with C3's rules, never without them"* — report, block and a
 * rate limit in the SAME change, because guideline 1.2 binds the public TestFlight link. An
 * identity has no moderation surface at all: it is your own name, your own face, and two answers
 * about your own privacy. MEASURED 2026-09-18: every community table holds **zero rows**, so
 * nothing here is gating anyone's use of an existing community — there is not one yet.
 *
 * ⚠️ **`community_identities` IS A NEW SURFACE AND NEVER A WIDENING OF `profiles`** — his decision.
 * RLS chooses ROWS and has no vocabulary for COLUMNS, so on a table readable by every signed-in
 * person the column list IS the security boundary: a field added there becomes readable by
 * everyone, with no policy change and nothing for a review to see.
 */
export function Identity({ foreground = true }: { foreground?: boolean }) {
  const [draft, setDraft] = useState<ConsentDraft>({
    name: "", visible: null, allowsMessages: null,
  });
  const [stored, setStored] = useState<{ is_visible: boolean; allows_messages: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  /* W13 — what the PERSON has done, which is what decides the fault's tone. Two flags rather
     than one: typing and pressing Save are different acts and either earns the alert.
     ⚠️ `attempted` LATCHES and is never cleared on a keystroke. A tone that fell back to a hint
     while somebody is correcting the very thing it is about would flicker between two registers
     mid-edit. */
  const [typed, setTyped] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => { if (foreground) heading.current?.focus(); }, [foreground]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // ⚠️ NO `.eq("id", …)`. `community_identities_owner_all` is the owner's own row and
      // `community_identities_read_visible` admits the visible ones; asking for "mine" by id here
      // would be a second statement of who I am.
      const { data: sessionData } = await supabase.auth.getSession();
      const me = sessionData.session?.user.id;
      if (me === undefined) { if (!cancelled) setLoading(false); return; }
      const { data, error: err } = await supabase
        .from("community_identities")
        .select("display_name, is_visible, allows_messages")
        .eq("id", me)
        .maybeSingle();
      if (cancelled) return;
      setLoading(false);
      if (err !== null) { setError(err.message); return; }
      if (data === null) return;
      setStored({ is_visible: data.is_visible, allows_messages: data.allows_messages });
      // ⚠️ SOMEBODY WHO HAS ALREADY ANSWERED STARTS AT THEIR STORED ANSWERS, which is not a
      // default — it is what they said last time, which is what a settings screen owes them.
      setDraft({
        name: data.display_name,
        visible: data.is_visible,
        allowsMessages: data.allows_messages,
      });
    })();
    return () => { cancelled = true; };
  }, []);

  const state = saveState(draft);

  async function save() {
    if (state.kind !== "ready") return;
    setBusy(true); setError(null); setSaved(false);
    const { data: sessionData } = await supabase.auth.getSession();
    const me = sessionData.session?.user.id;
    if (me === undefined) { setBusy(false); setError("You are signed out."); return; }
    // `id` defaults to `auth.uid()` and is NOT insertable — so it is not sent, and a row cannot be
    // written under somebody else's id even by trying.
    const { error: err } = await supabase
      .from("community_identities")
      .upsert({
        id: me,
        display_name: state.name,
        is_visible: state.visible,
        allows_messages: state.allowsMessages,
      });
    setBusy(false);
    if (err !== null) { setError(err.message); return; }
    setStored({ is_visible: state.visible, allows_messages: state.allowsMessages });
    setSaved(true);
  }

  async function withdraw() {
    setBusy(true); setError(null); setSaved(false);
    const { data: sessionData } = await supabase.auth.getSession();
    const me = sessionData.session?.user.id;
    if (me === undefined) { setBusy(false); setError("You are signed out."); return; }
    // ⚠️ AN UPDATE, NEVER A DELETE — DELETE on this table is granted to nobody, and the row
    // surviving is what lets somebody come back without re-consenting from scratch.
    const { error: err } = await supabase
      .from("community_identities").update(withdrawal()).eq("id", me);
    setBusy(false);
    if (err !== null) { setError(err.message); return; }
    setStored((s) => s === null ? s : { ...s, is_visible: false });
    setDraft((d) => ({ ...d, visible: false }));
  }

  return (
    <Workspace>
      {/* PRO WEB STEP 7 — NO COLUMN 2. It was `<ListNav items={[]} />`: an EMPTY 208 px column with its hairline, the shape
          Requests.tsx's own comment already names ("reads as a pane that failed to load"), and it pushed this tab's row to
          x = 464 while four of the six Account tabs start at 256 (MEASURED). */}
      <main id="main" className="account-page identity-page">
        <SettingsHead current="identity" headingRef={heading} />

        {loading && <p className="ht-callout loading-line" aria-live="polite">Loading…</p>}
        {error !== null && <p className="ht-callout identity-error" role="alert">{error}</p>}

        {!loading && (
          <>
            <p className="ht-callout identity-state">{describeIdentity(stored)}</p>
            <p className="ht-caption identity-note">
              This is separate from your coaching page and from your account. Nothing here is
              taken from your profile — you say what the community sees.
            </p>

            <label className="exercise-field identity-name">
              <span className="ht-caption">
                Name <span className="exercise-hint">up to {NAME_LIMIT} characters</span>
              </span>
              <input className="ht-input" value={draft.name}
                     onChange={(e) => { setTyped(true); setDraft({ ...draft, name: e.target.value }); }} />
            </label>

            {/* 🔴 W13 — THE SAME SENTENCE, IN THE REGISTER THE PERSON HAS EARNED.
                This was always a `role="alert"`, so the first thing a coach met on this screen
                was a screen-reader announcement in the error colour telling them off for not
                having typed a name yet. `faultTone` decides: a hint until they type or press
                Save, an alert after. It never goes silent on a real fault — a dead Save button
                with no reason is worse than one that shouts.
                ⚠️ AND IT SITS UNDER THE NAME FIELD, WHICH IT DID NOT (found by rendering,
                2026-09-19). It was written after BOTH consent questions, so `Pick a name people
                will see.` appeared directly beneath *Let people message me* and read as that
                question's hint — a sentence about one control sitting under a different one. A
                message attached to the wrong control is worse than no message: it is answerable
                advice about something that is not wrong. */}
            {state.kind === "nameFault" && (
              faultTone(true, { typed, attempted }) === "alert" ? (
                <p className="ht-callout identity-error" role="alert">
                  {nameFaultMessage(state.fault)}
                </p>
              ) : (
                <p className="ht-caption identity-note">{nameFaultMessage(state.fault)}</p>
              )
            )}

            {/* 🔴 BOTH QUESTIONS START UNANSWERED FOR SOMEBODY WHO HAS NEVER ANSWERED, and Save is
                unavailable until both are. A toggle that starts in a position is an answer nobody
                gave — and here the answer decides whether a real person's name is shown to
                strangers. Radios rather than checkboxes for exactly that reason: a checkbox has no
                way to be unanswered. */}
            <Consent
              legend="Show my name in the community"
              value={draft.visible}
              onChange={(v) => setDraft({ ...draft, visible: v })}
            />
            <Consent
              legend="Let people message me"
              value={draft.allowsMessages}
              onChange={(v) => setDraft({ ...draft, allowsMessages: v })}
            />

            {state.kind === "unanswered" && (
              <p className="ht-caption identity-note">Answer both questions to save.</p>
            )}
            {saved && <p className="ht-callout identity-note" role="status">Saved.</p>}

            <div className="identity-actions">
              <button type="button" className="ht-button ht-button-primary"
                      disabled={state.kind !== "ready" || busy}
                      onClick={() => { setAttempted(true); void save(); }}>
                {busy ? "Saving…" : stored === null ? "Publish my name" : "Save"}
              </button>
              {stored !== null && stored.is_visible && (
                <button type="button" className="ht-button ht-button-quiet" disabled={busy}
                        onClick={() => { void withdraw(); }}>
                  Hide my name
                </button>
              )}
            </div>
          </>
        )}
      </main>
    </Workspace>
  );
}

/** One consent question, in three states: yes, no, and NOT YET ANSWERED. */
function Consent({ legend, value, onChange }: {
  legend: string;
  value: boolean | null;
  onChange: (v: boolean) => void;
}) {
  const name = legend.replace(/\s+/g, "-").toLowerCase();
  return (
    <fieldset className="exercise-fieldset identity-consent">
      <legend className="ht-caption">{legend}</legend>
      <div className="exercise-checks">
        <label className="exercise-check">
          <input type="radio" name={name} checked={value === true}
                 onChange={() => onChange(true)} />
          <span>Yes</span>
        </label>
        <label className="exercise-check">
          <input type="radio" name={name} checked={value === false}
                 onChange={() => onChange(false)} />
          <span>No</span>
        </label>
      </div>
    </fieldset>
  );
}
